import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { DataProduct } from '@mtna/pojo-consumer-ui';

import { DURATION_LARGE_COLLAPSE, DURATION_LARGE_EXPAND, EASING_ACCELERATING, EASING_DECELERATING } from '../core/animations';

/**
 * Banner with either id or name and expansion panel for description.
 *
 * @author Zach Edwards <zach.edwards@mtna.us
 */
@Component({
  selector: 'rds-data-product-banner',
  template: `
    <header>
      <div fxLayout="row" fxLayoutAlign="space-between start">
        <h4 (click)="toggle()" role="button">{{ dataProduct?.name || dataProduct?.id || 'No data product selected' }}</h4>
        <h4 (click)="changeDataProduct.emit()" role="button">Change Data Product</h4>
      </div>
      <section [@slideInOut]="bannerOpen">
        <h3 *ngIf="hasMoreProperties">Description:</h3>
        <p class="mat-body-1" [innerHTML]="dataProduct?.description || 'No description available'"></p>
        <ng-container *ngIf="dataProduct?.citation">
          <h3>Citation:</h3>
          <p class="mat-body-1" [innerHTML]="dataProduct?.citation"></p>
        </ng-container>
        <ng-container *ngIf="dataProduct?.provenance">
          <h3>Provenance:</h3>
          <p class="mat-body-1" [innerHTML]="dataProduct?.provenance"></p>
        </ng-container>
        <ng-container *ngIf="dataProduct?.restriction">
          <h3>Restriction:</h3>
          <p class="mat-body-1" [innerHTML]="dataProduct?.restriction"></p>
        </ng-container>
        <ng-container *ngIf="dataProduct?.note">
          <h3>Note:</h3>
          <p class="mat-body-1" [innerHTML]="dataProduct?.note"></p>
        </ng-container>
      </section>
    </header>
  `,
  styleUrls: ['./data-product-banner.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      state('in', style({ height: '*', padding: '1rem' })),
      state('out', style({ overflow: 'hidden', height: '0px', padding: 0 })),
      transition('in => out', animate(`${DURATION_LARGE_EXPAND} ${EASING_DECELERATING}`)),
      transition('out => in', animate(`${DURATION_LARGE_COLLAPSE} ${EASING_ACCELERATING}`)),
    ]),
  ],
})
export class RdsDataProductBannerComponent {
  hasMoreProperties = true;

  @Input()
  set dataProduct(dataProduct: DataProduct) {
    this._dataProduct = dataProduct;

    this.hasMoreProperties =
      !!this._dataProduct &&
      (this._hasValue(dataProduct.citation) ||
        this._hasValue(dataProduct.note) ||
        this._hasValue(dataProduct.provenance) ||
        this._hasValue(dataProduct.restriction));
  }
  get dataProduct() {
    return this._dataProduct;
  }
  private _dataProduct: DataProduct;

  @Output() changeDataProduct = new EventEmitter<null>();

  bannerOpen: 'in' | 'out' = 'out';

  private _hasValue(value: any) {
    return value !== undefined && value !== null;
  }

  toggle() {
    this.bannerOpen = this.bannerOpen === 'in' ? 'out' : 'in';
  }
}
